import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Mail, RefreshCw } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; } };
const br = (d) => (d ? d.split("-").reverse().join("/") : "");
const LABELS = {
  cert_jobs: "Lotes de certificados", student_imports: "Rascunhos de importação",
  imports_rows: "Planilhas brutas", reset_tokens: "Tokens de senha", reset_requests: "Pedidos de senha",
};

function CleanupTable({ rows }) {
  if (!rows.length) return <p className="py-6 text-center text-sm text-slate-500" data-testid="cleanup-empty">Nenhuma limpeza executada ainda.</p>;
  return (
    <div className="overflow-x-auto">
      <Table data-testid="cleanup-table">
        <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Total</TableHead><TableHead>Detalhes</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`cleanup-row-${r.id}`}>
              <TableCell className="whitespace-nowrap">{fmt(r.created_at)}</TableCell>
              <TableCell className="font-semibold">{r.total ?? Object.values(r.result || {}).reduce((a, b) => a + b, 0)}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(r.result || {}).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="font-normal">{LABELS[k] || k}: {v}</Badge>
                  ))}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function SummaryTable({ rows }) {
  if (!rows.length) return <p className="py-6 text-center text-sm text-slate-500" data-testid="summary-empty">Nenhum resumo enviado ainda.</p>;
  return (
    <div className="overflow-x-auto">
      <Table data-testid="summary-table">
        <TableHeader><TableRow><TableHead>Escola</TableHead><TableHead>Período</TableHead><TableHead>Frequência</TableHead><TableHead>Faltas</TableHead><TableHead>Ocorrências</TableHead><TableHead>Envio</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid={`summary-row-${r.id}`}>
              <TableCell className="font-medium">{r.school_name}</TableCell>
              <TableCell className="whitespace-nowrap">{br(r.week_start)} a {br(r.week_end)}</TableCell>
              <TableCell>{r.stats?.frequencia != null ? `${r.stats.frequencia}%` : "—"}</TableCell>
              <TableCell>{r.stats?.faltas ?? 0}</TableCell>
              <TableCell>{r.stats?.ocorrencias_novas ?? 0}</TableCell>
              <TableCell>
                {r.sent_to?.length
                  ? <Badge className="bg-emerald-600">{r.sent_to.length} enviado(s)</Badge>
                  : <Badge variant="outline" className="text-amber-700">{r.recipients?.length ? "Falha no envio" : "Sem direção cadastrada"}</Badge>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Automacoes() {
  const [data, setData] = useState({ cleanup_runs: [], weekly_summaries: [], email_configured: true, retention_days: 30 });
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get("/automations").then((r) => setData(r.data)).catch(() => toast.error("Erro ao carregar automações")), []);
  useEffect(() => { load(); }, [load]);

  const runCleanup = async () => {
    setBusy("cleanup");
    try {
      const r = await api.post("/automations/cleanup");
      toast.success(`Limpeza concluída: ${Object.values(r.data).reduce((a, b) => a + b, 0)} item(ns) removido(s)`);
      load();
    } catch { toast.error("Falha ao executar limpeza"); } finally { setBusy(""); }
  };
  const runSummary = async () => {
    setBusy("summary");
    try {
      await api.post("/automations/weekly-summary");
      toast.success("Resumo semanal em envio para a direção das escolas");
      setTimeout(load, 4000);
    } catch { toast.error("Falha ao enviar resumo"); } finally { setBusy(""); }
  };

  return (
    <div data-testid="automacoes-page">
      <PageHeader title="Automações" subtitle="Tarefas agendadas da plataforma e seu histórico de execução">
        <Button variant="outline" onClick={load} data-testid="automacoes-refresh-btn"><RefreshCw className="mr-2 h-4 w-4" />Atualizar</Button>
      </PageHeader>
      <div className="grid min-w-0 grid-cols-1 gap-6">
        <Card className="min-w-0 p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg"><Mail className="h-5 w-5 text-orange-500" />Resumo semanal da direção</h2>
              <p className="text-sm text-slate-500">Toda segunda-feira às 7h (Brasília): frequência e ocorrências dos últimos 7 dias, por e-mail.</p>
              {!data.email_configured && <p className="mt-1 text-sm text-amber-700" data-testid="email-not-configured">E-mail não configurado — os resumos são registrados, mas não enviados.</p>}
            </div>
            <Button onClick={runSummary} disabled={busy === "summary"} className="bg-orange-500 hover:bg-orange-600" data-testid="run-summary-btn">Enviar agora</Button>
          </div>
          <SummaryTable rows={data.weekly_summaries} />
        </Card>
        <Card className="min-w-0 p-5">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg"><Trash2 className="h-5 w-5 text-orange-500" />Limpeza de dados temporários</h2>
              <p className="text-sm text-slate-500">Todo domingo às 3h (Brasília): remove dados temporários com mais de {data.retention_days} dias. Certificados são preservados.</p>
            </div>
            <Button variant="outline" onClick={runCleanup} disabled={busy === "cleanup"} data-testid="run-cleanup-btn">Executar agora</Button>
          </div>
          <CleanupTable rows={data.cleanup_runs} />
        </Card>
      </div>
    </div>
  );
}
